import React, { forwardRef, MouseEventHandler, Ref, useEffect, useState } from 'react';
import { ApiAssignmentUser } from '../../../api';
import { connect, ResolveThunks } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { Avatar, Button } from '@salvapp/ui-components';
import { AppState } from '../../../store/store';
import { getUserId } from '../../../auth/authReducer';
import { DropdownWrapper } from '../../DropdownWrapper';
import { InternalApiUserBasicDetails } from '../../../api-internal';
import { useMe } from '../../../organisation/useMe';
import { useAssignableUsers } from '../../../users/useAssignableUsers';

interface BulkAssignmentProps {
  defaultSize?: number;
  updateAlerts: (apiAssignmentUser: ApiAssignmentUser) => void;
}

export const BulkAssignment = ({ defaultSize = 40, updateAlerts }: ComponentProps) => {
  interface ToggleProps {
    onClick: MouseEventHandler;
  }

  const currentUser = useMe();
  const { assignableUsers } = useAssignableUsers();
  const [displayedAssignments, setDisplayedAssignments] = useState(assignableUsers?.slice(0, defaultSize));

  useEffect(() => {
    setDisplayedAssignments(assignableUsers?.slice(0, defaultSize));
  }, [assignableUsers, defaultSize]);

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<any>) => {
    const handleClick = (e: any) => {
      e.preventDefault();
      onClick(e);
    };

    return (
      <Button
        ref={ref}
        onClick={handleClick}
        size='medium'
        variant='white'
        className='btn-white dropdown dropdown-toggle'
      >
        Assign all
      </Button>
    );
  });

  Toggle.displayName = 'Toggle';

  const handleClick = async (user: InternalApiUserBasicDetails) => {
    updateAlerts({ id: user.id || 0 });
  };

  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setDisplayedAssignments(
      assignableUsers?.filter((user) => {
        return (
          user.email && (user.name.toLocaleLowerCase().includes(query) || user.email.toLowerCase().includes(query))
        );
      })
    );
  };

  return (
    <DropdownWrapper className='assignee'>
      <Dropdown.Toggle as={Toggle as any} id='chooseAssignee' />
      <Dropdown.Menu align={{ sm: 'start' }}>
        <div className='row px-4'>
          <div className='col'>
            <input
              data-testid='search'
              type='text'
              className='form-control form-control-sm'
              placeholder='Search ....'
              onChange={(e) => searchUsers(e)}
              autoFocus
            />
          </div>
          {!currentUser.isLoading && (
            <div className='col-auto'>
              <Button size='medium' variant='outline-primary' onClick={() => handleClick(currentUser)}>
                Assign to me
              </Button>
            </div>
          )}
        </div>
        <Dropdown.Divider />
        <div className='items'>
          {displayedAssignments?.map((user) => (
            <Dropdown.Item
              data-testid='dropdown-item'
              key={user.id}
              as='button'
              className='d-flex flex-row align-items-center'
              onClick={() => handleClick(user)}
            >
              <Avatar email={user.email} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>{user.name}</div>
                <div className='text-muted ms-2 small'>{user.email}</div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </DropdownWrapper>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    userId: getUserId(state),
  };
};

const mapDispatchToProps = {};

type ComponentProps = ReturnType<typeof mapStateToProps> &
  ResolveThunks<typeof mapDispatchToProps> &
  BulkAssignmentProps;

export default connect(mapStateToProps, mapDispatchToProps)(BulkAssignment);
